import type { ReactNode } from 'react';
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonBackButton } from '@ionic/react';

export interface LegalSection {
  id: string;
  heading: string;
  body: ReactNode;
}

/**
 * Shared renderer for the legal documents.
 *
 * Both the Privacy Policy and the Terms are long-form, mostly-static text, so
 * they share one layout instead of duplicating page chrome. Sections carry a
 * stable `id` so they can be deep-linked and so a change to one document does
 * not require touching the other.
 */
export default function LegalDocument({
  title,
  updated,
  intro,
  sections,
}: {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
}) {
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            {/* The app is a single-page shell, so the browser history is the
                only sensible "back" target for a public document. */}
            <IonBackButton defaultHref="/app" text="Back" />
          </IonButtons>
          <IonTitle>{title}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="page-shell legal">
          <p className="legal__updated">Last updated: {updated}</p>
          <p className="legal__intro">{intro}</p>
          <nav className="legal__toc" aria-label="Contents">
            {sections.map((section) => (
              <a key={section.id} href={`#${section.id}`}>{section.heading}</a>
            ))}
          </nav>
          {sections.map((section) => (
            <section key={section.id} id={section.id} className="legal__section">
              <h5>{section.heading}</h5>
              {section.body}
            </section>
          ))}
        </div>
      </IonContent>
    </IonPage>
  );
}